/**
 * What the calendar shows where the team defaults (school.calendarSettings)
 * set nothing: Baluu's defaults. One copy for the dashboard, which draws the
 * calendar from them (`calendar-shared-settings.ts`), and the API, which
 * compares against them (the personal-settings migration).
 *
 * Badges aren't listed: each badge's default is `defaultVisible` in
 * SESSION_BADGES. Listing colours default to none.
 */

/**
 * A compact card's defaults are what it showed before it had settings: spaces
 * only. Week, Month, the Day timeline and the Timeline type start the same.
 */
export const CALENDAR_VIEW_CARD_DEFAULTS = {
  location: false,
  tutorName: false,
  availability: true,
  showBadges: false
} as const;

/**
 * The Table type's columns, in the order they are drawn
 * (plans-done/calendar-view-types-roadmap.md, Phase 3). A view's `tableColumns`
 * is a sparse map of these to booleans, like the card field maps.
 */
export const CALENDAR_TABLE_COLUMNS = [
  "date",
  "time",
  "duration",
  "listing",
  "instructor",
  "location",
  "status",
  "capacity",
  "booked",
  "available",
  "waiting",
  "minimum",
  "tickets",
  "payments",
  "earnings",
  "notes"
] as const;
export type CalendarTableColumn = (typeof CALENDAR_TABLE_COLUMNS)[number];

/** What a run sheet needs first: when, what, who, where, and how full. */
export const CALENDAR_TABLE_COLUMN_DEFAULTS: Record<CalendarTableColumn, boolean> = {
  date: true,
  time: true,
  duration: false,
  listing: true,
  instructor: true,
  location: true,
  status: true,
  capacity: true,
  booked: true,
  available: false,
  waiting: false,
  minimum: false,
  tickets: false,
  payments: false,
  earnings: false,
  notes: false
};

/** How many days the Table type lists. */
export const CALENDAR_TABLE_RANGES = ["day", "week", "month"] as const;
export type CalendarTableRange = (typeof CALENDAR_TABLE_RANGES)[number];

/** How many days the Staff roster type spans. */
export const CALENDAR_ROSTER_RANGES = ["week", "fortnight"] as const;
export type CalendarRosterRange = (typeof CALENDAR_ROSTER_RANGES)[number];

/** Which sessions' bookings the Booking pipeline type shows: a day's, a week's or a month's. */
export const CALENDAR_PIPELINE_RANGES = ["day", "week", "month"] as const;
export type CalendarPipelineRange = (typeof CALENDAR_PIPELINE_RANGES)[number];

/** How many months the Term pipeline type spans: three or six. */
export const CALENDAR_TERMS_RANGES = ["quarter", "half"] as const;
export type CalendarTermsRange = (typeof CALENDAR_TERMS_RANGES)[number];

/** How many months the Multi-month horizon type spans. */
export const CALENDAR_HORIZON_MONTHS = [3, 6, 12] as const;
export type CalendarHorizonMonths = (typeof CALENDAR_HORIZON_MONTHS)[number];

/** Which sessions the Needs attention type checks: a day's, a week's or a month's. */
export const CALENDAR_ATTENTION_RANGES = ["day", "week", "month"] as const;
export type CalendarAttentionRange = (typeof CALENDAR_ATTENTION_RANGES)[number];

/**
 * What the Needs attention type lists, most urgent first: the order its
 * sessions' problems are ranked in. Booking problems (a request waiting on
 * the business, money owed) first, then the session's own (seats,
 * instructor, minimum, waitlist). The session problems are the calendar
 * summary strip's (`Session__NeedsAttention` in @obby/lib).
 */
export const CALENDAR_ATTENTION_KINDS = [
  "request-pending",
  "balance-due",
  "overbooked",
  "no-instructor",
  "below-minimum",
  "waitlist",
  "empty"
] as const;
export type CalendarAttentionKind = (typeof CALENDAR_ATTENTION_KINDS)[number];

/**
 * Which kinds the Needs attention type lists: every one. A field map like the
 * Table's columns, stored sparse, so a kind added later shows on views saved
 * before it.
 */
export const CALENDAR_ATTENTION_KIND_DEFAULTS = Object.fromEntries(
  CALENDAR_ATTENTION_KINDS.map(kind => [kind, true])
) as Record<CalendarAttentionKind, boolean>;

const CARD_DEFAULTS = {
  location: true,
  tutorName: true,
  availability: true,
  statusBadge: true,
  showBadges: true
} as const;

export const CALENDAR_SETTINGS_DEFAULTS = {
  fieldVisibility: CARD_DEFAULTS,
  mobileFieldVisibility: CARD_DEFAULTS,
  useSeparateMobileSettings: false,
  showAttendees: true,
  attendeeFieldVisibility: {
    email: true,
    phone: false,
    guestCount: true,
    ticketType: false,
    checkedIn: true,
    bookingAnswers: false
  },
  showCancelledSessions: true,
  fadePastSessions: true,
  showAvailabilitySlots: false,
  autoScrollToSession: true,
  highlightEmptySessions: false,
  // Linked-session highlighting (Phase 9): hovering a session lights its run.
  highlightLinkedSessions: true,
  showSummaryStrip: true,
  dayTimelineOrientation: "horizontal",
  colorBy: "listing",
  dayViewLayout: "timeline",
  dayRowGroupBy: "listing",
  // The Timeline type (plans-done/calendar-view-types-roadmap.md, Phase 2).
  timelineRange: "week",
  timelineGroupBy: "instructor",
  // The Table type (Phase 3).
  tableRange: "week",
  tableRegisterMode: false,
  tableColumns: CALENDAR_TABLE_COLUMN_DEFAULTS,
  // The Staff roster type (Phase 4).
  rosterRange: "week",
  // The Booking pipeline type (Phase 5).
  pipelineRange: "week",
  pipelineShowCancelled: false,
  // The Term pipeline type (Phase 6).
  termsRange: "quarter",
  // The Multi-month horizon type (Phase 7).
  horizonMonths: 3,
  // The Needs attention type (Phase 8).
  attentionRange: "week",
  attentionKinds: CALENDAR_ATTENTION_KIND_DEFAULTS,
  dayTimelineFieldVisibility: CALENDAR_VIEW_CARD_DEFAULTS,
  timelineFieldVisibility: CALENDAR_VIEW_CARD_DEFAULTS,
  weekFieldVisibility: CALENDAR_VIEW_CARD_DEFAULTS,
  monthFieldVisibility: CALENDAR_VIEW_CARD_DEFAULTS
} as const;
